import type { RunAction, RunSnapshot, StateChangeEntry } from './types.js';

/**
 * Client du worker de simulation.
 *
 * L'interface ne calcule RIEN : elle pilote et affiche. Toute la simulation vit
 * dans le Durable Object, ce qui permet de fermer l'onglet sans interrompre un
 * débarquement de plusieurs heures, et à deux personnes de regarder le même run.
 */

/**
 * Deux adresses, et la distinction est le cœur du dispositif.
 *
 * **Les COMMANDES** passent par l'API d'administration, qui relaie vers le
 * worker de simulation en y ajoutant, côté serveur, le jeton d'administration.
 * Ce jeton ne descend jamais dans le navigateur. Il l'était auparavant, compilé
 * via `VITE_SIMULATION_ADMIN_TOKEN` : n'importe qui ouvrant l'onglet réseau
 * pouvait le lire, et un jeton publié n'est plus un jeton.
 *
 * **Le FLUX** va directement au worker de simulation, en WebSocket. Il est en
 * lecture seule par construction — il n'expose que des instantanés déjà destinés
 * à l'affichage et n'accepte aucune commande. Le faire transiter par un relais
 * HTTP n'apporterait rien et coûterait une connexion de plus.
 */
const ADMIN_URL = import.meta.env['VITE_ADMIN_API_URL'] ?? 'http://127.0.0.1:8789';
const STREAM_URL = import.meta.env['VITE_SIMULATION_API_URL'] ?? 'http://127.0.0.1:8788';

/** Le jeton est celui de la session : le même utilisateur que l'administration. */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token === null || token.trim() === '' ? null : token.trim();
}

export function hasAccessToken(): boolean {
  return accessToken !== null;
}

function headers(): HeadersInit {
  return {
    'content-type': 'application/json',
    ...(accessToken === null ? {} : { authorization: `Bearer ${accessToken}` }),
  };
}

export interface CreateRunInput {
  seed: number;
  speedFactor: number;
  scenario: Record<string, unknown>;
  /**
   * « L'opérateur de la compagnie valide les sorties. »
   *
   * Un BOOLÉEN, pas une clé : le worker de simulation lit la sienne dans son
   * propre secret. Même doctrine que le jeton d'administration ci-dessus — une
   * clé qui descend dans le navigateur est lisible dans l'onglet réseau, et une
   * clé publiée n'est plus une clé. Sans cet indicateur, aucun run lancé depuis
   * l'écran ne peut produire de `confirmed` : les sorties opérateur ne partent
   * jamais.
   */
  externalSource: boolean;
  /**
   * Clé saisie à la main, pour le jour où l'on éprouve une clé qui n'est pas
   * celle du worker. Jamais conservée, jamais journalisée, jamais versionnée.
   */
  externalApiKey?: string;
}

export async function createRun(input: CreateRunInput): Promise<{ run_id: string }> {
  const response = await fetch(`${ADMIN_URL}/admin/v1/simulation`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({
      seed: input.seed,
      speed_factor: input.speedFactor,
      scenario: input.scenario,
      external_source: input.externalSource,
      ...(input.externalApiKey === undefined || input.externalApiKey === ''
        ? {}
        : { external_api_key: input.externalApiKey }),
    }),
  });
  if (!response.ok) throw new Error(`Création du run refusée (${String(response.status)})`);
  return (await response.json()) as { run_id: string };
}

/**
 * Ce que le serveur rend d'un plan de chargement déposé.
 *
 * `scenario_fragment` est la charge utile réelle : les immatriculations dans
 * l'ordre de débarquement prédit, prêtes à partir en `scenario`. Le reste est là
 * pour que l'utilisateur voie ce qu'il s'apprête à écrire.
 */
export interface ApercuManifeste {
  escale: string;
  format: 'xls' | 'csv';
  precision_horaire: 'seconde' | 'minute' | 'absente';
  entete: {
    navire: string | null;
    traversee: string | null;
    origine: string | null;
    destination: string | null;
    departTexte: string | null;
  };
  unites_lues: number;
  unites_retenues: number;
  sans_immatriculation: number;
  hors_perimetre: { section: string; unites: number }[];
  anomalies: { code: string; ligne: number; detail: string }[];
  scenario_fragment: {
    portCallReference: string;
    shipName: string | null;
    manifestTrailers: string[];
  };
  ecrit: boolean;
  remorques_inscrites?: number;
}

/**
 * Dépose un plan de chargement.
 *
 * Le fichier part BRUT : c'est le serveur qui le lit, avec le même code que
 * l'outil en ligne de commande. Le parser ici aurait dupliqué la lecture du
 * format, et les deux auraient fini par diverger sur un profil ajusté d'un seul
 * côté. Il n'est pas stocké — lu en mémoire, seule la donnée dérivée est écrite.
 *
 * `ecrire` à faux — le défaut — ne fait qu'un aperçu : un manifeste réécrit une
 * escale entière, on doit pouvoir regarder avant.
 */
export async function deposerManifeste(
  fichier: File,
  { ecrire = false }: { ecrire?: boolean } = {},
): Promise<ApercuManifeste> {
  const response = await fetch(
    `${ADMIN_URL}/admin/v1/manifest${ecrire ? '?ecrire=1' : ''}`,
    {
      method: 'POST',
      headers: {
        // Volontairement pas `application/json` : le corps est le fichier tel quel.
        'content-type': 'application/octet-stream',
        ...(accessToken === null ? {} : { authorization: `Bearer ${accessToken}` }),
      },
      body: await fichier.arrayBuffer(),
    },
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `Dépôt refusé (${String(response.status)})`);
  }
  return (await response.json()) as ApercuManifeste;
}

export async function controlRun(
  runId: string,
  action: RunAction,
  speedFactor?: number,
): Promise<void> {
  const response = await fetch(`${ADMIN_URL}/admin/v1/simulation/${runId}/control`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({
      action,
      ...(speedFactor === undefined ? {} : { speed_factor: speedFactor }),
    }),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Commande refusée (${String(response.status)})`);
  }
}

/**
 * Enrôle les boîtiers du run dans le registre lu par l'ingestion.
 *
 * Sans cette étape, le run tourne et s'affiche, mais toutes ses trames sont
 * refusées en `unknown_device` : le simulateur emprunte exactement le chemin
 * d'un boîtier réel, y compris son contrôle d'identité. C'est voulu — mais il
 * fallait un geste pour déclarer les clés, et il est ici.
 */
export async function enrollDevices(runId: string): Promise<{ enrolled: string[] }> {
  const response = await fetch(`${ADMIN_URL}/admin/v1/simulation/${runId}/enroll`, {
    method: 'POST',
    headers: headers(),
    body: '{}',
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Enrôlement refusé (${String(response.status)})`);
  }
  return (await response.json()) as { enrolled: string[] };
}

export async function fetchSnapshot(runId: string): Promise<RunSnapshot> {
  const response = await fetch(`${ADMIN_URL}/admin/v1/simulation/${runId}`, { headers: headers() });
  if (!response.ok) throw new Error(`Instantané indisponible (${String(response.status)})`);
  return (await response.json()) as RunSnapshot;
}

export async function fetchLog(runId: string, limit = 200): Promise<StateChangeEntry[]> {
  const response = await fetch(`${ADMIN_URL}/admin/v1/simulation/${runId}/log?limit=${String(limit)}`, {
    headers: headers(),
  });
  if (!response.ok) return [];
  const body = (await response.json()) as { entries: StateChangeEntry[] };
  return body.entries;
}

/**
 * Flux temps réel.
 *
 * WebSocket plutôt que sondage : à un tick par seconde, sonder gaspillerait des
 * requêtes Durable Object, dont le quota du plan free est justement ce qui borne
 * la durée des simulations.
 */
export function openStream(
  runId: string,
  onSnapshot: (snapshot: RunSnapshot) => void,
  onStatus: (connected: boolean) => void,
): () => void {
  const url = `${STREAM_URL.replace(/^http/u, 'ws')}/api/v1/runs/${runId}/stream`;
  let socket: WebSocket | null = null;
  let closed = false;
  let retryDelay = 1000;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const connect = (): void => {
    if (closed) return;
    socket = new WebSocket(url);

    socket.onopen = (): void => {
      onStatus(true);
      retryDelay = 1000;
    };

    socket.onmessage = (event: MessageEvent<string>): void => {
      try {
        const parsed = JSON.parse(event.data) as { type?: string; snapshot?: RunSnapshot };
        if (parsed.type === 'snapshot' && parsed.snapshot !== undefined)
          onSnapshot(parsed.snapshot);
      } catch {
        // Un message illisible ne doit pas rompre le flux.
      }
    };

    socket.onclose = (): void => {
      onStatus(false);
      if (closed) return;
      // Reconnexion à intervalle croissant : une coupure côté navigateur ne doit
      // pas marteler le worker.
      retryTimer = setTimeout(connect, retryDelay);
      retryDelay = Math.min(retryDelay * 2, 15000);
    };

    socket.onerror = (): void => socket?.close();
  };

  connect();

  return (): void => {
    closed = true;
    if (retryTimer !== null) clearTimeout(retryTimer);
    socket?.close();
  };
}
